import { AllocationMember } from '../types/allocation';
import type { MemberCosts } from '../services/memberCostService';

/**
 * Rate an allocated hour is costed at.
 *
 * Employee cost is what the member actually costs us, so it is the TMIS rate:
 * the month-specific cost from the employee cost sheet, falling back to the rate
 * the member was loaded with. The grid's "Hourly Rate for Client" is what we bill
 * for that hour, not what it costs, so it is deliberately not used here.
 */
export const getMemberRateForMonth = (
  member: AllocationMember | undefined,
  memberCosts: MemberCosts | undefined,
  monthStart: string
): number => {
  if (!member) return 0;

  const monthKey = monthStart.substring(0, 7); // "2025-10-01" -> "2025-10"
  const costsForMember = memberCosts?.[member.id.toString()];
  if (costsForMember && costsForMember[monthKey] !== undefined) {
    return costsForMember[monthKey];
  }

  return member.member.per_hour_cost || 0;
};

/** One member's cost for one month: allocated hours x the TMIS rate above. */
export const getMemberMonthlyCost = (
  member: AllocationMember | undefined,
  memberCosts: MemberCosts | undefined,
  monthStart: string
): number => {
  if (!member) return 0;
  const hours = member.monthlyAllocations[monthStart] || 0;
  return hours * getMemberRateForMonth(member, memberCosts, monthStart);
};

/**
 * Rate an allocated hour is billed at.
 *
 * The Team Allocation grid's "Hourly Rate for Client" is what we charge for that
 * hour. A member saved before that column existed has none, so we fall back to
 * what the hour costs - quoting at cost rather than inventing a margin.
 */
export const getMemberClientRateForMonth = (
  member: AllocationMember | undefined,
  memberCosts: MemberCosts | undefined,
  monthStart: string
): number => {
  if (!member) return 0;

  if (member.clientHourlyRate !== undefined && member.clientHourlyRate !== null) {
    return member.clientHourlyRate;
  }

  return getMemberRateForMonth(member, memberCosts, monthStart);
};

/** One member's revenue for one month: allocated hours x the client rate above. */
export const getMemberMonthlyRevenue = (
  member: AllocationMember | undefined,
  memberCosts: MemberCosts | undefined,
  monthStart: string
): number => {
  if (!member) return 0;
  const hours = member.monthlyAllocations[monthStart] || 0;
  return hours * getMemberClientRateForMonth(member, memberCosts, monthStart);
};
